import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, AlertCircle, AlertTriangle, Package, Sword, Warehouse,
  Lock, Wrench, Sparkles, LayoutGrid, List as ListIcon, Trash2, RotateCcw, Pencil,
} from 'lucide-react';
import {
  getInventory, getEquipment, getWarehouse,
  zeroItemSlots, undoZeroItemSlots,
  type ItemRow,
} from '@/shared/services/adminService';
import { API_URL } from '@/shared/services/authServices';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import { useToast } from '@/shared/contexts/ToastContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { ItemSlotEditModal } from './ItemSlotEditModal';

/** Ações de GM (multi-seleção). Zera 1+ itens de uma vez; backup automático (reversível).
    Vale pra inventário, equipamento e armazém — o backend exige tudo OFFLINE. */
interface GmActions {
  zero: (its: ItemRow[]) => void; undo: () => void;
  /** GM4 (feature edit_items): abre o modal de edição do slot (it=null = slot vazio). */
  edit?: (slot: number, it: ItemRow | null) => void;
}

type Source = 'inventory' | 'equipment' | 'warehouse';

interface Props {
  source: Source;
  /** Pra inventory/equipment passa characterIdx, pra warehouse passa userNum */
  id: number;
  /** classe do personagem (1-9) — usada só no equipment pra render do boneco central */
  classId?: number | null;
  /** Modo SELETOR (ex.: Personal Shop do bot): clicar num slot COM item chama onPick; sem ações de GM. */
  onPick?: (it: ItemRow) => void;
}

const SOURCE_LABELS: Record<Source, { label: string; icon: any }> = {
  inventory: { label: 'Inventário', icon: Package },
  equipment: { label: 'Equipados', icon: Sword },
  warehouse: { label: 'Armazém', icon: Warehouse },
};

// ── Grade: o Cabal usa 8 colunas × 8 linhas = 64 slots por aba/página.
//    O blob NÃO guarda "aba" (é convenção de UI do cliente); montamos a grade
//    a partir do campo `slot` que a SP devolve: pagina = slot//64, célula = slot%64.
//    (Ref lab: MODULO_09 §8 + cli-inventario-itens.md — INVENTORYSIZE=64, WAREHOUSE_SLOTNUM=512.)
const SLOTS_PER_PAGE = 64;                 // inventário/equip: 8 abas × 64 (8×8)
const WAREHOUSE_SLOTS_PER_PAGE = 128;      // armazém: 4 abas × 128 (8×16) — igual ao jogo
const GRID_COLS = 8;

/** slots por página (aba) depende do source: armazém tem abas bem maiores. */
function pageSize(source: Source): number {
  return source === 'warehouse' ? WAREHOUSE_SLOTS_PER_PAGE : SLOTS_PER_PAGE;
}

// ── Mapa REAL de slots do equipamento EP39 (descoberto no workflow 2026-07-02, regra de
//    ouro EP8 → Breaklee → EP39 real + banco). Cada posição da janela do jogo → índice de
//    slot real que a SP devolve. `slot: null` = índice ainda não cravado (o item, se existir,
//    aparece na faixa "Outros"). Doc: operacao/ep39-real/catalogos/equipamento-slots-EP39.md
interface EquipPos { slot: number | null; label: string }

// Aba 1 "Equipamento": coluna esquerda (2 col × 8 linhas) + coluna direita (2 col × 7 linhas).
const EQUIP_TAB1_L: EquipPos[] = [
  { slot: 4,  label: 'Arma D' },       { slot: 5,  label: 'Arma E' },
  { slot: 0,  label: 'Capacete' },     { slot: 1,  label: 'Armadura' },
  { slot: 2,  label: 'Luva' },         { slot: 3,  label: 'Botas' },
  { slot: 6,  label: 'Dragona' },      { slot: 10, label: 'Montaria' },
  { slot: 32, label: 'Cornalina' },    { slot: 19, label: 'Cinto' },
  { slot: 21, label: 'Minesta' },      { slot: 30, label: 'Talismã' },
  { slot: 33, label: 'Arcana Caos' },  { slot: 34, label: 'Arcana Ordem' },
  { slot: 50, label: 'Arcana Guard.' },
];
const EQUIP_TAB1_R: EquipPos[] = [
  { slot: 8,  label: 'Anel' },      { slot: 9,  label: 'Anel' },
  { slot: 17, label: 'Anel' },      { slot: 18, label: 'Anel' },
  { slot: 13, label: 'Brinco' },    { slot: 14, label: 'Brinco' },
  { slot: 15, label: 'Bracelete' }, { slot: 16, label: 'Bracelete' },
  { slot: 7,  label: 'Amuleto' },   { slot: 44, label: 'Broche' },
  { slot: 22, label: 'Efetor' },    { slot: 23, label: 'Efetor' },
  { slot: 45, label: 'Efetor' },    { slot: 46, label: 'Efetor' },
];
// Aba 2 "Arma de ligação": coluna esquerda (6) + direita (6).
// Artefatos EP37 = slots 47/48/49 (_39_Equipments). Insígnias = slots nativos
// 28 (Vida), 35 (Terra), 37 (Céus). Medalha = slot 40 (Honra de Nevareth).
const EQUIP_TAB2_L: EquipPos[] = [
  { slot: 47, label: 'Artefato' }, { slot: 48, label: 'Artefato' }, { slot: 49, label: 'Artefato' },
  { slot: 28, label: 'Insígnia' }, { slot: 35, label: 'Insígnia' }, { slot: 37, label: 'Insígnia' },
];
const EQUIP_TAB2_R: EquipPos[] = [
  { slot: 40, label: 'Medalha' },   { slot: null, label: 'Cubo Baldus' }, { slot: null, label: 'Force Wing?' },
  { slot: 41, label: 'Cubo' }, { slot: 42, label: 'Cubo' }, { slot: 43, label: 'Cubo' },
];

// Rótulo por índice de slot (pro tooltip/lista) + conjunto de slots com posição fixa conhecida.
const SLOT_LABELS: Record<number, string> = {};
for (const p of [...EQUIP_TAB1_L, ...EQUIP_TAB1_R, ...EQUIP_TAB2_L, ...EQUIP_TAB2_R]) {
  if (p.slot != null) SLOT_LABELS[p.slot] = p.label;
}
const MAPPED_SLOTS = new Set<number>(Object.keys(SLOT_LABELS).map(Number));

function equipSlotLabel(it: ItemRow): string {
  return (it.slot != null && SLOT_LABELS[it.slot]) || 'Equipado';
}

/** Item "suspeito": ID desconhecido (sem nome resolvido) ou id 0 — provável item
 *  corrompido que pode derrubar o char no login (o motivo do diagnóstico existir). */
function isSuspicious(it: ItemRow): boolean {
  if (!it) return false;
  if (it.itemId === 0) return true;
  const n = (it.itemName || '').trim();
  return n === '' || /^item\s*#?\d+$/i.test(n) || /unknown/i.test(n);
}

/** Cor de destaque da borda/nome, derivada do que a SP entrega (não temos "grade"
 *  crua, então sinalizamos por divine/extreme/upgrade). */
function accentColor(it: ItemRow): string {
  if (isSuspicious(it)) return '#f87171';           // vermelho — suspeito
  if ((it.divineLevel || 0) > 0) return '#fbbf24';  // âmbar — divine
  if ((it.extremeLevel || 0) > 0) return '#c084fc'; // violeta — extreme
  if ((it.upgradeLevel || 0) > 0) return '#38bdf8'; // azul — +N
  return 'rgba(255,255,255,0.92)';                  // branco legível — item comum
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

function pageLabel(source: Source, page: number): string {
  return `${source === 'warehouse' ? 'Página' : 'Aba'} ${ROMAN[page] || page + 1}`;
}

export function ItemsView({ source, id, classId, onPick }: Props) {
  const [items, setItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [hover, setHover] = useState<{ it: ItemRow; rect: DOMRect } | null>(null);
  const confirm = useConfirm();
  const { notify } = useToast();
  const { hasFeature } = useAuth();
  const canEdit = hasFeature('edit_items');
  const [editing, setEditing] = useState<{ slot: number; it: ItemRow | null } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = source === 'inventory' ? getInventory
               : source === 'equipment' ? getEquipment
               : getWarehouse;
      setItems(await fn(id));
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [source, id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Ações de GM (multi-seleção) — inventário/equipamento/armazém. Backend exige offline.
  const sourceLabelLow = source === 'warehouse' ? 'armazém' : source === 'equipment' ? 'equipamento' : 'inventário';
  const gmActions: GmActions = {
    zero: async (its: ItemRow[]) => {
      const picks = its.filter((it) => it.slot != null);
      if (picks.length === 0) return;
      const slots = picks.map((it) => it.slot as number);
      const ok = await confirm({
        title: picks.length === 1 ? 'Zerar este slot?' : `Zerar ${picks.length} slots?`,
        description: `Remove ${picks.length === 1 ? 'este item' : 'estes itens'} do ${sourceLabelLow}. Faz backup automático (reversível) e exige tudo OFFLINE. Use pra tirar item corrompido que derruba o char no login.`,
        body: (
          <ul className="text-sm space-y-1 max-h-52 overflow-auto">
            {picks.map((it) => (
              <li key={it.idx} className="flex items-center gap-1.5">
                <span className="text-white/35 font-mono text-xs">#{it.slot}</span>
                {(it.upgradeLevel || 0) > 0 && <b className="text-amber-400">+{it.upgradeLevel}</b>}
                <b className="text-white">{it.itemName || `Item ${it.itemId}`}</b>
                <span className="text-white/40 text-xs">(#{it.itemId})</span>
                {isSuspicious(it) && <span className="text-red-400 text-xs">⚠</span>}
              </li>
            ))}
          </ul>
        ),
        tone: 'danger',
        confirmLabel: picks.length === 1 ? 'Zerar slot' : `Zerar ${picks.length} slots`,
      });
      if (!ok) return;
      try {
        const res = await zeroItemSlots(source, id, slots, picks.map((it) => it.itemId));
        notify('success', res.message || 'Slots zerados');
        fetchData();
      } catch (err: any) {
        notify('error', err?.detail || err?.message || 'Erro ao zerar slot(s)');
      }
    },
    undo: async () => {
      try {
        const res = await undoZeroItemSlots(source, id);
        notify('success', res.message || 'Restaurado do backup');
        fetchData();
      } catch (err: any) {
        notify('error', err?.detail || err?.message || 'Nada pra desfazer');
      }
    },
    edit: canEdit ? (slot: number, it: ItemRow | null) => setEditing({ slot, it }) : undefined,
  };

  const meta = SOURCE_LABELS[source];
  const Icon = meta.icon;

  const q = filter.trim().toLowerCase();
  const matches = useCallback((it: ItemRow) =>
    !q || (it.itemName || '').toLowerCase().includes(q) || String(it.itemId).includes(q),
  [q]);

  const suspiciousCount = useMemo(() => items.filter(isSuspicious).length, [items]);

  return (
    <div>
      {/* Cabeçalho: título + contagem + aviso de suspeito + filtro + toggle de visão */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon size={16} className="text-[#00e1d7]" />
          <h4 className="text-sm font-bold text-white">{meta.label}</h4>
          <span className="text-[11px] text-white/45">
            ({items.length.toLocaleString('pt-BR')} itens)
          </span>
          {suspiciousCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold
                             text-red-300 bg-red-500/10 border border-red-500/30 rounded px-1.5 py-0.5">
              <AlertTriangle size={10} /> {suspiciousCount} suspeito{suspiciousCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrar por nome ou ID..."
            className="bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-1.5
                       text-[11px] text-white placeholder:text-white/40 w-48
                       focus:outline-none focus:border-[#00e1d7]/40"
          />
          <div className="flex items-center rounded-lg border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setView('grid')} title="Grade"
              className={`px-2 py-1.5 ${view === 'grid' ? 'bg-[#00e1d7]/15 text-[#00e1d7]' : 'text-white/40 hover:text-white/70'}`}
            ><LayoutGrid size={13} /></button>
            <button
              onClick={() => setView('list')} title="Lista"
              className={`px-2 py-1.5 ${view === 'list' ? 'bg-[#00e1d7]/15 text-[#00e1d7]' : 'text-white/40 hover:text-white/70'}`}
            ><ListIcon size={13} /></button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg p-3 mb-3 bg-red-500/10 border border-red-500/30 flex items-start gap-2">
          <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center text-white/40">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-white/40 text-xs">Sem itens</div>
      ) : view === 'list' ? (
        <ListView items={items} source={source} matches={matches} filtering={!!q} />
      ) : source === 'equipment' ? (
        <EquipmentView items={items} matches={matches} filtering={!!q}
          onHover={setHover} gm={gmActions} classId={classId} />
      ) : (
        <InventoryGrid items={items} source={source} matches={matches} filtering={!!q}
          onHover={setHover} gm={onPick ? null : gmActions} onPick={onPick} />
      )}

      {hover && <ItemTooltip it={hover.it} rect={hover.rect} source={source} />}

      {editing && (
        <ItemSlotEditModal open source={source} id={id} slot={editing.slot} item={editing.it}
          onClose={() => setEditing(null)} onSaved={fetchData} />
      )}
    </div>
  );
}

// ────────────────────────────── Multi-seleção + barra de GM ──────────────────────────────

/** Estado de multi-seleção por slot (pos_vis). Reseta quando os itens recarregam. */
function useSlotSelection(items: ItemRow[]) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  useEffect(() => { setSelected(new Set()); }, [items]);
  const toggle = useCallback((slot: number) => {
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(slot)) n.delete(slot); else n.add(slot);
      return n;
    });
  }, []);
  const clear = useCallback(() => setSelected(new Set()), []);
  return { selected, toggle, clear };
}

/** Barra de ação de GM: lista os slots selecionados, zera todos, limpa e desfaz. */
function GmZeroBar({
  items, selected, gm, onClear,
}: {
  items: ItemRow[]; selected: Set<number>; gm: GmActions; onClear: () => void;
}) {
  const slots = useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected]);
  const picks = useMemo(
    () => items.filter((it) => it.slot != null && selected.has(it.slot)),
    [items, selected],
  );
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
      {slots.length > 0 ? (
        <>
          <span className="text-white/50">Slots:</span>
          <span className="font-mono text-white/85">{slots.join(', ')}</span>
          <span className="text-white/40">({slots.length})</span>
          <button onClick={() => gm.zero(picks)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25">
            <Trash2 size={12} /> Zerar selecionado{slots.length > 1 ? 's' : ''}
          </button>
          {gm.edit && slots.length === 1 && (
            <button onClick={() => gm.edit!(slots[0], picks[0] || null)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#00e1d7]/10 border border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20">
              <Pencil size={12} /> Editar item
            </button>
          )}
          <button onClick={onClear}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/55 hover:text-white/80">
            Limpar seleção
          </button>
        </>
      ) : (
        <span className="text-white/35">Clique nos itens pra selecionar (multi-seleção) e liberar o Zerar (o char precisa estar offline).</span>
      )}
      <button onClick={gm.undo}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/55 hover:text-white/80 ml-auto">
        <RotateCcw size={12} /> Desfazer último
      </button>
    </div>
  );
}

// ────────────────────────────── Grade (inventário / armazém) ──────────────────────────────

function InventoryGrid({
  items, source, matches, filtering, onHover, gm, onPick,
}: {
  items: ItemRow[]; source: Source;
  matches: (it: ItemRow) => boolean; filtering: boolean;
  onHover: (h: { it: ItemRow; rect: DOMRect } | null) => void;
  gm?: GmActions | null;
  onPick?: (it: ItemRow) => void;
}) {
  // slot → item (o campo slot da SP é a posição absoluta; pode ter buracos = slots vazios)
  const bySlot = useMemo(() => {
    const m = new Map<number, ItemRow>();
    for (const it of items) if (it.slot != null) m.set(it.slot, it);
    return m;
  }, [items]);

  const PSIZE = pageSize(source);   // armazém = 128/aba (4 abas), resto = 64/aba (8 abas)

  const maxSlot = useMemo(
    () => items.reduce((mx, it) => Math.max(mx, it.slot ?? 0), 0),
    [items],
  );
  const pageCount = Math.max(1, Math.floor(maxSlot / PSIZE) + 1);

  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [source]);

  const { selected, toggle, clear } = useSlotSelection(items);

  // Contagem de itens por página (pro rótulo das abas)
  const perPage = useMemo(() => {
    const arr = new Array(pageCount).fill(0);
    for (const it of items) if (it.slot != null) arr[Math.floor(it.slot / PSIZE)]++;
    return arr;
  }, [items, pageCount, PSIZE]);

  const base = page * PSIZE;

  return (
    <div>
      {/* Painel estilo janela do jogo: as abas ocupam a largura TODA do painel
          (igual "I II III…VIII" no jogo) e a grade de 8 colunas PREENCHE o
          container — itens grandes e fiéis. */}
      <div className="rounded-xl bg-[#080c11] border border-white/[0.06] p-2 w-full max-w-[560px] mx-auto">
        {pageCount > 1 && (
          <div className="grid gap-1 mb-2"
               style={{ gridTemplateColumns: `repeat(${pageCount}, minmax(0,1fr))` }}>
            {Array.from({ length: pageCount }, (_, p) => (
              <button
                key={p} onClick={() => setPage(p)}
                title={`${pageLabel(source, p)} — ${perPage[p]} itens`}
                className={`flex flex-col items-center justify-center py-1 rounded border transition-colors ${
                  p === page
                    ? 'bg-[#00e1d7]/15 border-[#00e1d7]/50 text-[#00e1d7]'
                    : 'bg-[#0d141b] border-white/[0.06] text-white/45 hover:text-white/75'
                }`}
              >
                <span className="text-[11px] font-bold leading-none">{ROMAN[p] || p + 1}</span>
                <span className="text-[9px] opacity-60 leading-none mt-0.5">{perPage[p]}</span>
              </button>
            ))}
          </div>
        )}

        <div className="grid gap-1"
             style={{ gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0,1fr))` }}>
          {Array.from({ length: PSIZE }, (_, c) => {
            const slot = base + c;
            const it = bySlot.get(slot) || null;
            return (
              <SlotCell
                key={slot} it={it} fill
                dim={!!it && filtering && !matches(it)}
                onHover={onHover}
                onClick={onPick ? (it ? () => onPick(it) : undefined)
                  : gm && it && it.slot != null ? () => toggle(it.slot as number)
                  : (!it && gm?.edit ? () => gm.edit!(slot, null) : undefined)}
                selected={!!it && it.slot != null && selected.has(it.slot)}
              />
            );
          })}
        </div>
      </div>

      {gm && !onPick && (
        <div className="w-full max-w-[560px] mx-auto">
          <GmZeroBar items={items} selected={selected} gm={gm} onClear={clear} />
        </div>
      )}
    </div>
  );
}

// ────────────────────────────── Equipamento (2 abas, estilo jogo) ──────────────────────────────

function EquipmentView({
  items, matches, filtering, onHover, gm, classId,
}: {
  items: ItemRow[];
  matches: (it: ItemRow) => boolean; filtering: boolean;
  onHover: (h: { it: ItemRow; rect: DOMRect } | null) => void;
  gm?: GmActions | null;
  classId?: number | null;
}) {
  const [tab, setTab] = useState<0 | 1>(0);
  const { selected, toggle, clear } = useSlotSelection(items);

  const bySlot = useMemo(() => {
    const m = new Map<number, ItemRow>();
    for (const it of items) if (it.slot != null) m.set(it.slot, it);
    return m;
  }, [items]);

  // Equipados cujo slot ainda não tem posição fixa mapeada → faixa "Outros" (nada some).
  const outros = useMemo(
    () => items.filter((it) => it.slot == null || !MAPPED_SLOTS.has(it.slot)),
    [items],
  );

  const cell = (p: EquipPos, key: number) => {
    const it = p.slot != null ? bySlot.get(p.slot) || null : null;
    return (
      <SlotCell key={key} it={it} emptyLabel={p.label} size={58}
        dim={filtering && !!it && !matches(it)} onHover={onHover}
        onClick={gm && it && it.slot != null ? () => toggle(it.slot as number)
          : (!it && p.slot != null && gm?.edit ? () => gm.edit!(p.slot as number, null) : undefined)}
        selected={!!it && it.slot != null && selected.has(it.slot)} />
    );
  };

  const [colL, colR] = tab === 0 ? [EQUIP_TAB1_L, EQUIP_TAB1_R] : [EQUIP_TAB2_L, EQUIP_TAB2_R];

  const tabBtn = (n: 0 | 1, Icon: any, label: string) => (
    <button onClick={() => setTab(n)}
      className={`flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors ${
        tab === n ? 'bg-[#00e1d7]/15 border-[#00e1d7]/50 text-[#00e1d7]' : 'bg-[#0d141b] border-white/[0.06] text-white/45 hover:text-white/75'}`}>
      <Icon size={12} /> {label}
    </button>
  );

  return (
    <div>
      <div className="rounded-xl p-4 w-fit mx-auto border border-white/[0.06]"
        style={{ background: 'linear-gradient(180deg,#0e151d,#080c11)' }}>
        {/* Abas estilo jogo — ocupam a largura toda do painel */}
        <div className="flex items-stretch gap-2 mb-4">
          {tabBtn(0, Sword, 'Equipamento')}
          {tabBtn(1, Sparkles, 'Arma de ligação')}
        </div>

        <div className="flex gap-3 items-start">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 58px)', gap: 6 }}>
            {colL.map((p, n) => cell(p, n))}
          </div>
          <EquipDoll classId={classId} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 58px)', gap: 6 }}>
            {colR.map((p, n) => cell(p, n))}
          </div>
        </div>

        {outros.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/[0.05]">
            <div className="text-[9px] text-white/35 mb-1.5 uppercase tracking-wide">
              Outros equipados <span className="text-white/25 normal-case">(slot sem posição fixa mapeada)</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 58px)', gap: 6 }}>
              {outros.map((it) => (
                <SlotCell key={it.idx} it={it} size={58} dim={filtering && !matches(it)} onHover={onHover}
                  onClick={gm && it.slot != null ? () => toggle(it.slot as number) : undefined}
                  selected={it.slot != null && selected.has(it.slot)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {gm && (
        <div className="w-fit mx-auto min-w-[320px]">
          <GmZeroBar items={items} selected={selected} gm={gm} onClear={clear} />
        </div>
      )}
    </div>
  );
}

/** Boneco central — arte da classe do personagem (1-9) recortada. Fallback pro
 *  placeholder quando a classe é desconhecida ou a imagem falha. */
function EquipDoll({ classId }: { classId?: number | null }) {
  const [ok, setOk] = useState(true);
  const valid = classId != null && classId >= 1 && classId <= 9;
  return (
    <div className="rounded-xl flex flex-col items-center justify-center overflow-hidden self-stretch"
      style={{ width: 200, minHeight: 356, background: 'radial-gradient(120% 55% at 50% 12%, rgba(0,225,215,0.06), transparent 60%), #0a0e14', border: '1px solid rgba(255,255,255,0.06)' }}>
      {valid && ok ? (
        <img src={`/class/${classId}.png`} alt="" onError={() => setOk(false)}
          className="max-w-full max-h-[420px] object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.6)]" />
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 px-4">
          <div style={{ width: 58, height: 172, background: 'rgba(255,255,255,0.06)', borderRadius: '38px 38px 22px 22px / 54px 54px 18px 18px', position: 'relative' }}>
            <span style={{ position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)', width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          </div>
          <div className="text-[9px] text-white/30 text-center leading-relaxed">boneco 3D<br />(render do cliente)</div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────── Célula de slot ──────────────────────────────

function SlotCell({
  it, emptyLabel, size = 56, fill, dim, onHover, onClick, selected,
}: {
  it: ItemRow | null; emptyLabel?: string; size?: number; fill?: boolean; dim?: boolean;
  onHover: (h: { it: ItemRow; rect: DOMRect } | null) => void;
  onClick?: () => void; selected?: boolean;
}) {
  // fill = preenche a coluna do grid (quadrado responsivo — itens grandes como no jogo).
  // fixo = usa `size` em px (usado no equipamento com posições mapeadas).
  const boxCls = fill ? 'w-full aspect-square' : '';
  const boxSize = fill ? undefined : { width: size, height: size };
  if (!it) {
    return (
      <div
        className={`rounded-md bg-black/70 border border-white/[0.05] flex items-center justify-center ${boxCls}
          ${onClick ? 'cursor-pointer hover:border-[#00e1d7]/40 hover:bg-[#00e1d7]/[0.06]' : ''}`}
        style={boxSize}
        onClick={onClick}
        title={onClick ? 'Colocar item neste slot' : undefined}
      >
        {emptyLabel && (
          <span className="text-[8px] text-white/20 text-center leading-tight px-0.5 select-none">
            {emptyLabel}
          </span>
        )}
      </div>
    );
  }

  const upgrade = it.upgradeLevel || 0;
  const suspicious = isSuspicious(it);
  const accent = accentColor(it);
  const shadow = selected
    ? '0 0 0 2px #00e1d7'
    : suspicious ? '0 0 0 1px rgba(248,113,113,0.35), 0 0 10px rgba(248,113,113,0.25)' : undefined;

  return (
    <div
      className={`relative rounded-md overflow-hidden transition-transform hover:scale-[1.06] hover:z-10 ${boxCls} ${onClick ? 'cursor-pointer' : 'cursor-help'}`}
      style={{
        ...boxSize,
        background: '#000000',
        border: `1.5px solid ${accent}`,
        boxShadow: shadow,
        opacity: dim ? 0.28 : 1,
      }}
      onMouseEnter={(e) => onHover({ it, rect: e.currentTarget.getBoundingClientRect() })}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
    >
      <SlotIcon itemId={it.cleanItemId ?? it.itemId} size={size} />

      {/* +N no canto superior esquerdo */}
      {upgrade > 0 && (
        <span className="absolute top-0 left-0 px-0.5 text-[9px] font-bold leading-none
                         text-amber-300 bg-black/70 rounded-br">
          +{upgrade}
        </span>
      )}
      {/* aviso de suspeito */}
      {suspicious && (
        <span className="absolute top-0 right-0 bg-black/70 rounded-bl p-0.5">
          <AlertTriangle size={10} className="text-red-400" />
        </span>
      )}
      {/* faixinhas de bind/selado/quebrado no rodapé */}
      <div className="absolute bottom-0 right-0 flex gap-0.5 p-0.5">
        {it.isBroken === 1 && <span className="w-1.5 h-1.5 rounded-full bg-red-400" title="Quebrado" />}
        {it.isSealed === 1 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Selado" />}
        {(it.bindCharacter === 1 || it.bindAccount === 1) && (
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" title="Vinculado" />
        )}
      </div>
    </div>
  );
}

/** Ícone dentro do slot — mesmo endpoint da API, escalado pra preencher o quadro. */
export function SlotIcon({ itemId, size }: { itemId: number; size: number }) {
  const [ok, setOk] = useState(true);
  useEffect(() => { setOk(true); }, [itemId]);
  if (!ok) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Package size={Math.round(size * 0.4)} className="text-white/20" />
      </div>
    );
  }
  return (
    <img
      src={`${API_URL}/admin/item-icon/${itemId}.png?v=nobg2`}
      onError={() => setOk(false)}
      alt=""
      className="absolute inset-0 w-full h-full object-contain object-center p-0.5"
      loading="lazy"
    />
  );
}

// ────────────────────────────── Tooltip do item (hover) ──────────────────────────────

function ItemTooltip({ it, rect, source }: { it: ItemRow; rect: DOMRect; source: Source }) {
  const W = 250, H = 260, M = 10;
  let left = rect.right + 8;
  if (left + W > window.innerWidth - M) left = rect.left - W - 8;
  if (left < M) left = M;
  let top = rect.top;
  if (top + H > window.innerHeight - M) top = window.innerHeight - H - M;
  if (top < M) top = M;

  const upgrade = it.upgradeLevel || 0;
  const extreme = it.extremeLevel || 0;
  const divine = it.divineLevel || 0;
  const suspicious = isSuspicious(it);
  const forceNames = (it.slotForceNames || [])
    .filter(Boolean).filter((n) => !/unknown/i.test(n as string)) as string[];
  const forceOpts = (it.slotOptions || []).filter(Boolean) as string[];
  const craftValid = it.craftLevel && it.craftLevel > 0
    && it.craftName && !/unknown/i.test(it.craftName);
  const slotLabel = source === 'equipment'
    ? equipSlotLabel(it)
    : `${pageLabel(source, Math.floor((it.slot ?? 0) / pageSize(source)))} · #${((it.slot ?? 0) % pageSize(source)) + 1}`;

  return (
    <div
      className="fixed z-50 pointer-events-none rounded-lg bg-[#0d141b] border border-white/10
                 shadow-2xl p-3 text-xs"
      style={{ left, top, width: W }}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        {upgrade > 0 && <span className="text-amber-400 font-bold">+{upgrade}</span>}
        <span className="font-bold" style={{ color: accentColor(it) }}>
          {it.itemName || `Item ${it.itemId}`}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-white/45">
        <span className="font-mono">#{it.itemId}</span>
        <span className="text-[#00e1d7]/85">{slotLabel}</span>
        {it.duration && it.duration > 0 ? <span className="text-yellow-400">{it.duration}d</span> : null}
        {extreme > 0 && <span className="text-purple-400">EX {extreme}</span>}
        {divine > 0 && <span className="text-pink-400">DV {divine}</span>}
      </div>

      {suspicious && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-red-300
                        bg-red-500/10 border border-red-500/25 rounded px-1.5 py-1">
          <AlertTriangle size={11} className="shrink-0" />
          Item suspeito (ID desconhecido) — possível causa de queda no login.
        </div>
      )}

      {(it.bindAccount === 1 || it.bindCharacter === 1 || it.isSealed === 1 || it.isBroken === 1) && (
        <div className="mt-2 flex items-center gap-2 flex-wrap text-[9px]">
          {it.bindAccount === 1 && (
            <span className="inline-flex items-center gap-0.5 text-blue-400"><Lock size={9} /> Conta</span>
          )}
          {it.bindCharacter === 1 && (
            <span className="inline-flex items-center gap-0.5 text-purple-400"><Lock size={9} /> Char</span>
          )}
          {it.isSealed === 1 && <span className="text-amber-400">Selado</span>}
          {it.isBroken === 1 && <span className="text-red-400">Quebrado</span>}
        </div>
      )}

      {(forceNames.length > 0 || forceOpts.length > 0) && (
        <div className="mt-2 flex items-start gap-1.5 text-[10px] flex-wrap">
          <Sparkles size={10} className="text-[#00e1d7] mt-0.5 shrink-0" />
          {(forceNames.length > 0 ? forceNames : forceOpts).map((n, i) => (
            <span key={i} className="px-1.5 rounded bg-[#00e1d7]/10 border border-[#00e1d7]/20 text-[#00e1d7]/85">
              {n}
            </span>
          ))}
        </div>
      )}

      {craftValid && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[10px]">
          <Wrench size={10} className="text-orange-400 shrink-0" />
          <span className="text-orange-400/85">Craft +{it.craftLevel} · {it.craftName}</span>
        </div>
      )}

      {/* Option crua (número) — o decode de slot ainda esta sendo corrigido; o
          numero em si sempre aparece pra referencia/debug. */}
      {it.optionData != null && (
        <div className="mt-2 pt-1.5 border-t border-white/[0.06] text-[9px] text-white/45 font-mono break-all">
          <span className="text-white/30 uppercase tracking-wider">Option:</span> {it.optionData}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────── Lista (visão antiga, mantida) ──────────────────────────────

function ListView({
  items, source, matches, filtering,
}: {
  items: ItemRow[]; source: Source;
  matches: (it: ItemRow) => boolean; filtering: boolean;
}) {
  const shown = filtering ? items.filter(matches) : items;
  if (shown.length === 0) {
    return <div className="py-8 text-center text-white/40 text-xs">Nenhum item bate o filtro</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[500px] overflow-y-auto">
      {shown.map((it) => <ItemCard key={it.idx} it={it} source={source} />)}
    </div>
  );
}

function ItemCard({ it, source }: { it: ItemRow; source: Source }) {
  const upgrade = it.upgradeLevel || 0;
  const extreme = it.extremeLevel || 0;
  const divine = it.divineLevel || 0;
  const suspicious = isSuspicious(it);
  const slotLabel = source === 'equipment'
    ? equipSlotLabel(it)
    : `${pageLabel(source, Math.floor((it.slot ?? 0) / pageSize(source)))} · #${((it.slot ?? 0) % pageSize(source)) + 1}`;

  const slotsActive = (it.slotOptions || []).filter(Boolean);
  const slotForceNames = (it.slotForceNames || [])
    .filter(Boolean).filter((n) => !/unknown/i.test(n as string));
  const craftValid = it.craftLevel && it.craftLevel > 0
    && it.craftName && !/unknown/i.test(it.craftName);

  return (
    <div className="bg-[#0d141b] rounded-lg p-3 text-xs"
      style={{ border: `1px solid ${suspicious ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.04)'}` }}>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded-md bg-[#0a0e14] border border-white/[0.04]
                        overflow-hidden shrink-0 relative">
          <SlotIcon itemId={it.cleanItemId ?? it.itemId} size={80} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                {upgrade > 0 && <span className="text-amber-400 font-bold">+{upgrade}</span>}
                <span className="font-bold truncate" style={{ color: accentColor(it) }}>
                  {it.itemName || `Item ${it.itemId}`}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[10px] text-white/45">
                <span className="font-mono">#{it.itemId}</span>
                <span className="text-[#00e1d7]/85">{slotLabel}</span>
                {it.duration && it.duration > 0 ? <span className="text-yellow-400">{it.duration}d</span> : null}
                {extreme > 0 && <span className="text-purple-400">EX {extreme}</span>}
                {divine > 0 && <span className="text-pink-400">DV {divine}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5 text-[9px] shrink-0">
              {it.bindAccount === 1 && (
                <span className="text-blue-400 inline-flex items-center gap-0.5"><Lock size={9} /> Conta</span>
              )}
              {it.bindCharacter === 1 && (
                <span className="text-purple-400 inline-flex items-center gap-0.5"><Lock size={9} /> Char</span>
              )}
              {it.isSealed === 1 && <span className="text-amber-400">Selado</span>}
              {it.isBroken === 1 && <span className="text-red-400">Quebrado</span>}
            </div>
          </div>

          {slotsActive.length > 0 && (
            <div className="mt-2 flex items-start gap-1.5 text-[10px] flex-wrap">
              <Sparkles size={10} className="text-[#00e1d7] mt-0.5 shrink-0" />
              {slotForceNames.length > 0
                ? slotForceNames.map((n, i) => (
                    <span key={i} className="px-1.5 rounded bg-[#00e1d7]/10 border border-[#00e1d7]/20 text-[#00e1d7]/85">{n}</span>
                  ))
                : slotsActive.map((opt, i) => (
                    <span key={i} className="px-1.5 rounded bg-white/[0.04] border border-white/[0.06] text-white/55 font-mono">{opt}</span>
                  ))}
            </div>
          )}

          {craftValid && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[10px]">
              <Wrench size={10} className="text-orange-400 shrink-0" />
              <span className="text-orange-400/85">Craft +{it.craftLevel} · {it.craftName}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
